//
// # Modal
//
// A reusable modal overlay component.
// Renders as a React portal into document.body so it sits above all other UI.
// Closes on backdrop click or Escape key.
//

import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: React.ReactNode;
  // When false, the modal cannot be dismissed via the X button, the Escape
  // key, or backdrop click. Defaults to true. Set to false for unrecoverable
  // states (e.g. the ReconnectDialog) where dismissing without resolving
  // would leave the app in a broken state.
  readonly dismissible?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  dismissible = true,
}: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!dismissible) return;
    // Only close if the click is on the backdrop itself, not the card
    if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Prevent keyboard events from reaching EditorCore's global key handler,
  // which would otherwise interpret them as canvas shortcuts (e.g. 'F' zooms to fit).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (dismissible && e.key === "Escape") {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={contentRef}
        className="w-full max-w-md rounded-lg border shadow-xl"
        style={{
          backgroundColor: "var(--color-ide-surface)",
          borderColor: "var(--color-ide-border)",
          color: "var(--color-ide-text)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: "var(--color-ide-border)" }}
        >
          <h2 className="text-sm font-semibold">{title}</h2>
          {dismissible && (
            <button
              onClick={onClose}
              className="flex items-center justify-center w-6 h-6 rounded hover:bg-white/10"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-4 py-4">{children}</div>
      </div>
    </div>,
    document.body
  );
}
